---
title: Epics
lede: A piece of work too big for one session, run as subtasks, one after the other or side by side, written by hand or planned by Agent.
order: 31
part: Plan the work
---

An epic is one card on the Board that holds a set of subtasks, each run in its own session. **In order**, each subtask starts once the one before it has landed. **Side by side**, they run at the same time, each landing on its own.

![An epic card with its subtasks](media/epic-card.webp)

### Write an epic

On the Board, press **Create** and pick **Epic**, or pick **New epic** from the navigator's **+**. The epic page opens.

1. Give it a title and, if you like, a goal the subtasks share.
2. Under it, write each subtask: a title and its instructions. **Add subtask** adds one; an epic holds two to twelve.
3. Choose how they run from the chip in the page's bar: **In order** or **Side by side**.
4. Press **Create epic**. One card lands in **Backlog**, and nothing runs until you start it.

| Mode | How the subtasks run |
|---|---|
| **In order** | One after the other: each starts from the branch the one before it landed on. The default |
| **Side by side** | Each from the epic's base branch, landing on its own, as many at once as your session limit allows. One that fails stops none of the others |

The mode can change until a subtask has started.

### Plan it with Agent

On the Board, press **Create** and pick **Epic with Agent**. The [Agent panel](/agent-panel) opens on the Board's page.

1. Say what the epic is for. **Write an epic** begins the sentence for you; **Cover the least-tested code** and **Pay down the worst debt** ask for an area to improve.
2. To cut an epic from a list you already have, such as findings, a report or tickets, use **Attach files** above the composer and pick **An epic from a file**: one subtask per item, each on its own.
3. Tick the tasks you want, then press **Write N tasks**.

Agent reads the repositories as committed in a throwaway micro-VM. It reads; it runs nothing. It also proposes the mode: side by side when each subtask stands alone. One ticked task is written as an ordinary task; two or more become an epic, up to twelve. Either way it lands in **Backlog**, so you read it before anything runs.

![Agent's plan for an epic: six subtasks to tick, then Write 6 tasks](media/epic-plan.webp)

The Agent panel also works on the epic page itself: **Cut it into subtasks** plans from the title and goal you wrote, and **Subtasks from a file** takes an attached list. What you tick fills the page; nothing reaches the board until you create or save it.

### Read before it runs

Open the epic to read or change it. A subtask is editable until it starts; after that it is a record of what its session was given, and clicking it opens its task. **Save** writes your changes, **Revert** takes them back.

Each subtask is a task on the workspace's defaults: its source branch, model and effort, with no workflow.

### Run it

Press **Start** on the epic page, or move the card to **To do**. **Start** and **Pause** refuse while the page holds unsaved changes: save first. From then on it moves itself:

1. The epic goes to **In development**, and its subtasks join the **To do** queue: the first one **In order**, as many as your limit allows **Side by side**.
2. Each subtask starts like any task when a slot is free.
3. **In order**, once a subtask has landed (merged), the next one is queued, cut from the branch that now holds the work before it. **Side by side**, a new one is queued as each slot frees.
4. When every subtask has landed, the epic goes to **Waiting**.

Without automatic landing, each subtask waits in **Waiting** for your review and merge. With **Settings › Git & merge › When a session is finished** on, subtasks land by themselves. See [Agents that run agents](/agents-run-agents#land-a-finished-session-by-itself).

### Follow it

The card counts **N of M landed** (and says **side by side** when it is) and lists each subtask with where it stands: **landed**, **running**, **waiting for your review**, **next** (or **queued**, side by side), **stopped**, or still to come. A running subtask has **Open**, and one waiting for you has **Review**; both open its session.

In the Board's **List** view, the epic's chevron unfolds its subtasks under it.

### When it stops

**In order**, a subtask that fails to start, or is closed without landing, stops the chain. **Side by side**, the others carry on, and once nothing is left to run the epic names the ones that did not land. Either way, press **Take up again** to try them again.

To pause, press **Pause**, or move the epic back to **Backlog**. The subtasks under way finish, then no other starts until you start the epic again.

### Archive and delete

| You want to | Rule |
|---|---|
| Move it to **In development** or **Waiting** by hand | Refused: an epic moves itself |
| Archive it | Only once every subtask has landed: **Archive** on the epic page or the card's menu |
| Delete it | Refused while a subtask is queued, running or waiting for review. Deleting takes the subtasks still in the backlog with it; landed ones stay as records |
